import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Trombinoscope from '@/pages/Trombinoscope';
import DailyReportForm from '@/pages/DailyReportForm';
import AgentReportForm from '@/pages/AgentReportForm';
import Annuaire from '@/pages/Annuaire';
import MainCourante from '@/pages/MainCourante';
import Consignes from '@/pages/Consignes';
import DocTerrain from '@/pages/DocTerrain';
import Messagerie from '@/pages/Messagerie';
import MessagerieEvoluee from '@/pages/MessagerieEvoluee';
import Profile from '@/pages/Profile';
import ActivityHistory from '@/pages/ActivityHistory';
import AdminPanel from '@/pages/AdminPanel';
import PendingApproval from '@/pages/PendingApproval';
import SiteSelector from '@/pages/SiteSelector';
import ResetPassword from '@/pages/ResetPassword';
import CharteAcceptation from '@/components/CharteAcceptation';
import Charte from '@/pages/Charte';
import CharteRedacteurGate from '@/components/CharteRedacteurGate';
import SetPinModal from '@/components/SetPinModal';
import EndOfServiceWarningModal from '@/components/EndOfServiceWarningModal';
import { Toaster } from '@/components/ui/sonner';

const App = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${storedToken}`
            }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(storedToken);
          } else {
            localStorage.removeItem('token');
            setToken(null);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Revérifie le statut du compte à heures fixes (peu après les deux délais de
  // fin de service, 21h00 et 08h30 - voir check_missed_end_of_service côté
  // serveur), pour que le popup d'avertissement apparaisse sans attendre un
  // rechargement complet de la page si l'onglet est resté ouvert.
  useEffect(() => {
    if (!token) return undefined;
    const CHECK_TIMES = [{ h: 21, m: 5 }, { h: 8, m: 35 }];
    const timers = [];

    const recheckUser = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        if (response.ok) setUser(await response.json());
      } catch { /* silencieux */ }
    };

    const scheduleNext = (time) => {
      const now = new Date();
      const next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), time.h, time.m, 0, 0);
      if (next <= now) next.setDate(next.getDate() + 1);
      const timer = setTimeout(async () => {
        await recheckUser();
        scheduleNext(time);
      }, next.getTime() - now.getTime());
      timers.push(timer);
    };

    CHECK_TIMES.forEach(scheduleNext);
    return () => timers.forEach(clearTimeout);
  }, [token]);

  const handleLogin = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Un compte en attente de validation par un administrateur n'a accès
  // à aucune page de l'application, quelle que soit l'URL demandée.
  if (token && user && user.approved === false) {
    return <PendingApproval user={user} onLogout={handleLogout} />;
  }

  // Charte d'utilisation et d'information RGPD : signature manuscrite
  // obligatoire à la première connexion et à chaque nouvelle version. Le
  // serveur refuse aussi toute autre requête tant qu'elle n'est pas signée.
  if (token && user && user.approved !== false && !user.charte_ok) {
    return (
      <>
        <Toaster position="bottom-right" />
        <CharteAcceptation user={user} onAccepted={(updatedUser) => setUser(updatedUser)} onLogout={handleLogout} />
      </>
    );
  }

  // Un compte affecté à plusieurs sites (et non administrateur) peut avoir un
  // rôle différent selon le site (chef de poste sur l'un, agent sur l'autre) :
  // il doit choisir sur quel site il travaille avant d'accéder à l'application.
  const needsSiteSelection = token && user && user.approved !== false
    && user.role !== 'admin'
    && (user.assigned_sites || []).length > 1
    && !user.active_site_id;

  if (needsSiteSelection) {
    return <SiteSelector user={user} onSiteSelected={handleLogin} onLogout={handleLogout} />;
  }

  return (
    <BrowserRouter>
      <Toaster position="bottom-right" />
      {/* Compte de site partagé : charte à signer par l'agent rédacteur */}
      {token && user && <CharteRedacteurGate />}
      {/* Pop-up obligatoire de création du code personnel à 4 chiffres, pour
          les agents, chefs de poste et administrateurs (seuls rôles pouvant
          être désignés comme rédacteur sur un compte de site partagé), tant
          qu'il n'a pas été créé */}
      {token && user && user.approved !== false
        && ['agent', 'chef_poste', 'admin'].includes(user.role) && !user.pin_set && (
        <SetPinModal user={user} onCreated={(updatedUser) => setUser(updatedUser)} />
      )}
      {/* Avertissement : une fin de service a été clôturée automatiquement
          faute d'avoir été réalisée dans les délais (voir backend) */}
      {token && user && user.approved !== false && user.pin_set
        && ['agent', 'chef_poste', 'admin'].includes(user.role) && user.pending_end_of_service_alert && (
        <EndOfServiceWarningModal user={user} onAcknowledged={(updatedUser) => setUser(updatedUser)} />
      )}
      <Routes>
        <Route
          path="/login"
          element={!token ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/charte" element={<Charte />} />
        <Route
          path="/"
          element={token ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/main-courante"
          element={token ? <MainCourante user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/trombinoscope"
          element={token ? <Trombinoscope user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/reporting"
          element={token ? <DailyReportForm user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/comptes-rendus"
          element={token ? <AgentReportForm user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/annuaire"
          element={token ? <Annuaire user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/consignes"
          element={token ? <Consignes user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/doc-terrain"
          element={token && (['admin', 'chef_poste'].includes(user?.role) || user?.permission_overrides?.view_doc_terrain === true) ? <DocTerrain user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
        />
        <Route
          path="/messagerie"
          element={token ? <MessagerieEvoluee user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
        />
        <Route
          path="/profile"
          element={token ? <Profile user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/activity"
          element={token && (user?.role === 'admin' || user?.permission_overrides?.view_activity_history === true) ? <ActivityHistory user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
        />
        <Route
          path="/admin"
          element={token && (user?.role === 'admin' || ['manage_sites', 'manage_users', 'approve_accounts', 'view_activity_history'].some(k => user?.permission_overrides?.[k] === true)) ? <AdminPanel user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
        />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
